// @ts-nocheck - temporary until React types are aligned (JSX children strictness)
import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { installSessionExpiredInterceptors, setSessionExpiredHandler } from './utils/sessionExpired';
import Home from './components/home/Home';
import Login from './components/auth/Login';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import VoteRoomPrecheckConfirmPage from './components/public/VoteRoomPrecheckConfirmPage';
import { PrivacyPage, TermsPage } from './components/auth/LegalPages';
import { ErrorBoundary } from './components/shell/ErrorBoundary';
import { Toaster } from './components/shared/Toaster';
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './components/admin/Dashboard';
import ClientLicenses from './components/admin/ClientLicenses';
import FirmLayout from './components/firm/FirmLayout';
import { FirmCompaniesProvider } from './components/firm/FirmCompaniesContext';
import { FirmLawFirmProvider } from './components/firm/FirmLawFirmContext';
import FirmDashboard from './components/firm/Dashboard';
import Companies from './components/firm/Companies';
import CompanyDetail from './components/firm/CompanyDetail';
import FirmUsers from './components/firm/FirmUsers';
import { CompanyProvider, ClientLayout } from './components/client';
import AnalysesPage from './components/client/pages/Analyses';
import OverviewPage from './components/client/pages/Overview';
import OverviewById from './components/client/pages/OverviewById';
import TransactionPage from './components/client/pages/Transaction';
import IndividualsPage from './components/client/pages/Individuals';
import CompensationPage from './components/client/pages/Compensation';
import BaseAmountPage from './components/client/pages/BaseAmount';
import ParachutePaymentsPage from './components/client/pages/ParachutePayments';
import AnalysisResultsPage from './components/client/pages/AnalysisResults';
import RunHistoryPage from './components/client/pages/RunHistory';
import RunSnapshotPage from './components/client/pages/RunSnapshot';
import DocumentsPage from './components/client/pages/Documents';
import ArchivedPage from './components/client/pages/Archived';
import SettingsPage from './components/client/pages/Settings';
import FirmSettings from './components/firm/FirmSettings';
import AdminSettings from './components/admin/AdminSettings';
import AdminRootUsers from './components/admin/AdminRootUsers';

/** When JWT/session is rejected (401), clear auth and send user to the right sign-in screen. */
function SessionExpiredHandler() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    installSessionExpiredInterceptors();
    const onExpire = () => {
      const loginType = userRef.current?.loginType;
      logout();
      if (loginType === 'Root') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    };
    setSessionExpiredHandler(onExpire);
    return () => setSessionExpiredHandler(null);
  }, [navigate, logout]);

  return null;
}

function CompanyHomeRoute() {
  const navigate = useNavigate();
  return <Home onLogin={() => navigate('/login')} showLoginInline={false} onLoginSuccess={() => {}} />;
}

/** Old firm URL: /firm/client-analyses/:id → /firm/companies/:id */
function LegacyClientAnalysesRedirect() {
  const { id } = useParams();
  return <Navigate to={`/firm/companies/${id}`} replace />;
}

/** Old firm URL: /firm/clients → /firm/companies */
function LegacyFirmClientsListRedirect() {
  return <Navigate to="/firm/companies" replace />;
}

/** Old firm URL: /firm/clients/:id → /firm/companies/:id */
function LegacyFirmClientDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/firm/companies/${id}`} replace />;
}

function CompanyLoginRoute() {
  const navigate = useNavigate();
  return <Login onBack={() => navigate('/')} onSuccess={() => navigate('/client/analyses')} />;
}

/** Legacy client URL prefix: redirect `/company/...` → `/client/...` */
function LegacyCompanyToClientRedirect() {
  const location = useLocation();
  const nextPath = location.pathname.replace(/^\/company/, '/client');
  const next = `${nextPath}${location.search}${location.hash || ''}`;
  return <Navigate to={next} replace />;
}

function AdminLoginRoute() {
  const navigate = useNavigate();
  return <Login onBack={() => navigate('/')} onSuccess={() => navigate('/admin')} />;
}

/** Shared routes reachable while signed in or out (password reset, precheck confirm link). */
const publicAccountRoutes = (
  <>
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/vote-room/prelaunch/confirm-email" element={<VoteRoomPrecheckConfirmPage />} />
  </>
);

function AppRouter() {
  const { user } = useAuth();

  if (user) {
    // Route by loginType only (single source of truth; avoids role/loginType drift).
    if (user.loginType === 'Firm') {
      return (
        <Routes>
          {publicAccountRoutes}
          <Route
            path="/firm"
            element={
              <FirmLawFirmProvider>
                <FirmCompaniesProvider>
                  <FirmLayout />
                </FirmCompaniesProvider>
              </FirmLawFirmProvider>
            }
          >
            <Route index element={<FirmDashboard />} />
            <Route path="companies" element={<Companies />} />
            <Route path="companies/:id" element={<CompanyDetail />} />
            <Route path="clients" element={<LegacyFirmClientsListRedirect />} />
            <Route path="clients/:id" element={<LegacyFirmClientDetailRedirect />} />
            <Route path="users" element={<FirmUsers />} />
            <Route path="settings" element={<FirmSettings />} />
            <Route path="client-analyses" element={<Navigate to="/firm/companies" replace />} />
            <Route path="client-analyses/:id" element={<LegacyClientAnalysesRedirect />} />
          </Route>
          <Route path="*" element={<Navigate to="/firm" replace />} />
        </Routes>
      );
    }
    if (user.loginType === 'Root') {
      return (
        <Routes>
          {publicAccountRoutes}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="licenses" element={<ClientLicenses />} />
            <Route path="users" element={<AdminRootUsers />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      );
    }
    return (
      <Routes>
        {publicAccountRoutes}
        <Route
          path="/client"
          element={
            <CompanyProvider>
              <ClientLayout />
            </CompanyProvider>
          }
        >
          <Route index element={<OverviewPage />} />
          <Route path="analyses" element={<AnalysesPage />} />
          <Route path="transaction" element={<TransactionPage />} />
          <Route path="individuals" element={<IndividualsPage />} />
          <Route path="compensation" element={<CompensationPage />} />
          <Route path="base-amount" element={<BaseAmountPage />} />
          <Route path="parachute" element={<ParachutePaymentsPage />} />
          <Route path="analysis" element={<AnalysisResultsPage />} />
          <Route path="run-history/:analysisId/:runId" element={<RunSnapshotPage />} />
          <Route path="run-history" element={<RunHistoryPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="archives" element={<ArchivedPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path=":id" element={<OverviewById />} />
        </Route>
        <Route path="/company/*" element={<LegacyCompanyToClientRedirect />} />
        <Route path="*" element={<Navigate to="/client/analyses" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<CompanyHomeRoute />} />
      <Route path="/login" element={<CompanyLoginRoute />} />
      <Route path="/admin" element={<AdminLoginRoute />} />
      <Route path="/admin/login" element={<AdminLoginRoute />} />
      {publicAccountRoutes}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <SessionExpiredHandler />
          <AppRouter />
          <Toaster />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}
